import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
  FilterChangedEvent,
} from 'ag-grid-community'
import { getRequests, getRequestsSummary } from '../../shared/api/requests.api'
import { useRequestsUrlFilters } from './hooks/useRequestsUrlFilters'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { requestsRoleFlags } from './hooks/useRequestsRoleFlags'
import { useAuthStore } from '../auth/authStore'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import {
  STATUS_OPTIONS,
  STATUS_LABELS,
  formatSum,
  formatDate,
  defaultColDef as baseDefaultColDef,
  buildRequestsColDefs,
} from './components/requestsColDefs'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { useGridFilterStats } from './hooks/useGridFilterStats'
import { useAgGridScrollRestoration } from '../../shared/hooks/useScrollRestoration'
import { ActiveSummaryLabel, ProcessedSummaryLabel } from './components/RequestsSummaryLabels'
import { useRequestsColumnPersist } from './hooks/useRequestsColumnPersist'
import { RequestsTrashModal } from './components/RequestsTrashModal'

ModuleRegistry.registerModules([AllCommunityModule])

/** «2026-07-01» → «01.07.2026» строково, без Date-парсинга (TZ-сдвиг). */
function fmtDateOnly(d: string): string | null {
  const [y, m, day] = d.split('-')
  return y && m && day ? `${day}.${m}.${y}` : null
}

export function RequestsPage() {
  // URL-фильтры: статус/набор/менеджер/пресет + период создания (из аналитики).
  const {
    status,
    statuses,
    managerId,
    preset,
    createdFrom,
    createdTo,
    deadlineTo,
    hasUrlFilters,
    resetUrlFilters,
    setStatusFilter,
  } = useRequestsUrlFilters()

  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const { canDelete, showManagerColumn, isProcurement, isSalesView, isRosView } = requestsRoleFlags(
    user?.role,
  )
  // «Корзина» (мягко удалённые заявки): модалка со списком и восстановлением.
  const [trashOpen, setTrashOpen] = useState(false)

  const [searchText, setSearchText] = useState('')
  // Серверный поиск с дебаунсом 300мс: запрос идёт после паузы в наборе —
  // не дёргаем бэк на каждый символ. Локальный AG Grid quickFilter
  // продолжает работать по уже загруженным строкам (двухуровневый поиск:
  // мгновенный по локальным + догружаем серверные совпадения).
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 300)
    return () => clearTimeout(t)
  }, [searchText])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'requests',
      {
        status,
        statuses,
        managerId,
        preset,
        createdFrom,
        createdTo,
        deadlineTo,
        search: debouncedSearch,
      },
    ],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      getRequests({
        status: status || undefined,
        statuses: statuses || undefined,
        managerId: managerId || undefined,
        filter: preset ?? undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        deadlineTo: deadlineTo || undefined,
        search: debouncedSearch || undefined,
        cursor: pageParam,
        // 400 за запрос — крупными пачками: меньше догрузок, меньше дёрганья
        // блока «Обработанные», обработанные набираются быстрее.
        limit: 400,
      }),
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
  })

  // Сводка для шапки: точные счётчики и суммы КП (не зависят от того,
  // сколько страниц инфинит-скролла уже догрузилось).
  const { data: summary } = useQuery({
    queryKey: ['requests-summary'],
    queryFn: getRequestsSummary,
    enabled: isSalesView,
  })

  // Счётчик активных + сумма КП, пересчитывается по отфильтрованным строкам
  // грида (статус/менеджер/клиент/поиск). Когда фильтров нет — берём точный
  // серверный итог из summary, иначе считаем по видимым строкам.
  // Счётчики по видимым (после фильтров) строкам — активные и обработанные.
  const { stats: activeStats, recompute: recomputeActiveStats } = useGridFilterStats()
  const { stats: processedStats, recompute: recomputeProcessedStats } = useGridFilterStats()

  const allItems = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const gridRef = useRef<AgGridReact>(null)
  const processedGridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)

  // Восстановление позиции скролла таблиц заявок при возврате из карточки —
  // отдельно для активного и для блока «Обработанные».
  const activeGridContainerRef = useRef<HTMLDivElement>(null)
  const processedGridContainerRef = useRef<HTMLDivElement>(null)
  useAgGridScrollRestoration('requests-grid', activeGridContainerRef)
  useAgGridScrollRestoration('requests-processed-grid', processedGridContainerRef)
  const {
    onCellMouseDown: onCellMouseDownProcessed,
    onCellMouseOver: onCellMouseOverProcessed,
    getCellStyle: getCellStyleProcessed,
  } = useGridSelection(processedGridRef)
  const defaultColDef = useMemo(
    () => ({ ...baseDefaultColDef, cellStyle: getCellStyle }),
    [getCellStyle],
  )
  const defaultColDefProcessed = useMemo(
    () => ({ ...baseDefaultColDef, cellStyle: getCellStyleProcessed }),
    [getCellStyleProcessed],
  )
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        if (!hasNextPage || isFetchingNextPage) return
        if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
          void fetchNextPage()
        }, 300)
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onDeleted = useCallback(() => {
    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
    void queryClient.resetQueries({ queryKey: ['requests'] })
  }, [queryClient])

  const rowData = useMemo(
    () =>
      allItems.map((req) => {
        const latestQuotation = req.quotationSnapshots?.[0]
        const quotationSum = latestQuotation?.totalSum ?? null
        const orderedSum = req.orderedSum ?? 0
        const procTotal = req.procQuotesTotal ?? 0
        const procByMe = req.procQuotesByMe ?? 0
        const completionRows = req.procurementCompletions ?? []
        const completionVisible = completionRows.reduce(
          (sum, row) => sum + row.visibleItemsTotal,
          0,
        )
        const completionProcessed = completionRows.reduce(
          (sum, row) => sum + row.processedItemsTotal,
          0,
        )
        const completedRows = completionRows.filter((row) => row.isCompleted).length
        const completionRequested = completionRows.reduce(
          (sum, row) => sum + (row.requestedItemsTotal ?? 0),
          0,
        )
        let procurementProgressLabel = 'Не начато'
        let procurementProgressTone = 'gray'
        if (completionRows.length > 0 && completionVisible > 0) {
          if (completedRows === completionRows.length) {
            procurementProgressLabel = 'Просчитано'
            procurementProgressTone = 'green'
          } else if (completionProcessed > 0) {
            procurementProgressLabel = 'Частично просчитано'
            procurementProgressTone = 'amber'
          } else if (completionRequested > 0) {
            // Закупщик скачал BOM и отправил запросы поставщикам, но ещё ни
            // одной позиции не обработал — менеджеру видно, что закупка пошла.
            procurementProgressLabel = 'Запрошено у поставщиков'
            procurementProgressTone = 'blue'
          } else {
            procurementProgressLabel = 'В работе'
            procurementProgressTone = 'blue'
          }
        }
        return {
          id: req.id,
          clientName: req.client?.name ?? '—',
          clientCode: req.client?.code ?? '—',
          requestNumber: req.requestNumber ?? '—',
          type: req.type === 'TEKUCHKA' ? 'Текучка' : 'Проект',
          deadline: req.deadline ?? null,
          deadlineLabel: req.deadline ? formatDate(req.deadline) : '',
          followUpDueAt: req.followUps?.[0]?.dueAt ?? null,
          followUpLabel: req.followUps?.[0] ? formatDate(req.followUps[0].dueAt) : '',
          followUpNote: req.followUps?.[0]?.note ?? null,
          status: req.status,
          statusLabel: STATUS_LABELS[req.status] ?? req.status,
          itemsCount: req._count?.items ?? 0,
          quotationSum,
          quotationSumLabel: quotationSum == null ? 'КП нет' : formatSum(quotationSum),
          orderedSum,
          managerName: req.manager?.name ?? req.manager?.email ?? '—',
          createdAt: req.createdAt,
          createdAtLabel: formatDate(req.createdAt),
          procQuotesTotal: procTotal,
          procQuotesByMe: procByMe,
          procQuotesLabel: `${procByMe} / ${procTotal}`,
          procQuotesDone: procTotal > 0 && procByMe >= procTotal,
          procurementProgressLabel,
          procurementProgressTone,
          _req: req,
        }
      }),
    [allItems],
  )

  const colDefs = useMemo(
    () => buildRequestsColDefs({ canDelete, showManagerColumn, isProcurement, onDeleted }),
    [canDelete, showManagerColumn, isProcurement, onDeleted],
  )

  const [showProcessed, setShowProcessed] = useState(false)
  const processedSectionRef = useRef<HTMLDivElement | null>(null)

  // Кнопка «Обработанные заявки» в шапке: раскрывает блок и прокручивает
  // к нему так, чтобы его шапка встала к верху видимой области. setTimeout —
  // чтобы дождаться рендера таблицы перед scrollIntoView.
  const openProcessed = useCallback(() => {
    setShowProcessed(true)
    setTimeout(() => {
      processedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }, [])
  // «Обработанные» для закупщика — READY (все квоты даны) и REJECTED.
  const isProcessedProc = (s: string | undefined): boolean => s === 'READY' || s === 'REJECTED'
  // «Обработанные» для продажников — заявки закрытые В заказ / В отказ / В архив.
  const isProcessedSales = (s: string): boolean =>
    s === 'CLOSED_WON' || s === 'CLOSED_LOST' || s === 'ARCHIVED'
  const activeRows = useMemo(() => {
    if (isProcurement) return rowData.filter((r) => !isProcessedProc(r._req.procurementStatus))
    if (isSalesView || isRosView) return rowData.filter((r) => !isProcessedSales(r.status))
    return rowData
  }, [rowData, isProcurement, isSalesView, isRosView])
  const processedRows = useMemo(() => {
    if (isProcurement) return rowData.filter((r) => isProcessedProc(r._req.procurementStatus))
    if (isSalesView || isRosView) return rowData.filter((r) => isProcessedSales(r.status))
    return []
  }, [rowData, isProcurement, isSalesView, isRosView])

  // Активны ли фильтры (статус/набор/менеджер/пресет/период/поиск). При фильтрах
  // счётчики считаем по видимым строкам грида, иначе — точный серверный итог.
  const baseFiltersOn = !!status || hasUrlFilters || searchText.trim() !== ''

  // Per-user сохранение раскладки колонок + воронок-фильтров (localStorage).
  const { saveState, onGridReady } = useRequestsColumnPersist(user?.id)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-900">Заявки</h1>
          {!isLoading && (
            <ActiveSummaryLabel
              filtersOn={baseFiltersOn || activeStats.filtered}
              stats={activeStats}
              summary={summary}
            />
          )}
        </div>
        {user?.role !== 'GENERAL_DIRECTOR' && (
          <Link
            to="/requests/new"
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            + Новая заявка
          </Link>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Поиск: заявка, клиент, партномер, бренд…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[200px] max-w-md"
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className="text-xs text-gray-400 hover:text-gray-700"
            title="Очистить поиск"
          >
            × Очистить
          </button>
        )}
        <select
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {(createdFrom || createdTo) && (
          // Конвенционная плашка активного URL-фильтра (как в ClientsPage).
          // Даты форматируем строково (не через new Date) — date-only строка
          // в западных таймзонах уехала бы на день назад.
          <span className="flex items-center gap-2 text-xs bg-brand-50 border border-brand-100 rounded-lg px-3 py-1.5 whitespace-nowrap">
            <span className="text-gray-600">Создано:</span>
            <span className="px-2 py-0.5 bg-white rounded-md border border-brand-200 text-gray-800">
              {fmtDateOnly(createdFrom) ?? '…'} — {fmtDateOnly(createdTo) ?? '…'}
            </span>
          </span>
        )}
        {hasUrlFilters && (
          <button
            onClick={resetUrlFilters}
            className="px-3 py-1.5 border border-brand-300 bg-brand-50 text-brand-700 rounded-lg text-sm hover:bg-brand-100 transition-colors whitespace-nowrap"
            title="Сбросить фильтр по менеджеру, периоду и срезу с дашборда/аналитики"
          >
            × Сбросить фильтр
          </button>
        )}
        {(isProcurement || isSalesView || isRosView) && !isLoading && rowData.length > 0 && (
          <button
            onClick={openProcessed}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700"
          >
            Обработанные заявки ({summary?.processed.count ?? processedRows.length})
          </button>
        )}
        {/* «Корзина»: мягко удалённые заявки, восстановление 30 дней.
            Закупщикам (PROCUREMENT/ROS) не показываем — они заявки не удаляют. */}
        {(isSalesView || user?.role === 'MANAGER') && (
          <button
            onClick={() => setTrashOpen(true)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700"
            title="Удалённые заявки: восстановление в течение 30 дней"
          >
            🗑 Корзина
          </button>
        )}
      </div>
      {trashOpen && (
        <RequestsTrashModal
          canRestore={canDelete}
          canPurge={user?.role === 'ADMIN'}
          onClose={() => setTrashOpen(false)}
        />
      )}

      {isLoading ? (
        <PageLoader />
      ) : !rowData.length ? (
        <EmptyState title="Заявок не найдено" />
      ) : (
        <>
          <div
            ref={activeGridContainerRef}
            data-testid="requests-grid"
            className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-220px)]"
          >
            <AgGridReact
              ref={gridRef}
              rowData={activeRows}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              localeText={AG_GRID_LOCALE_RU}
              // Локальный quickFilterText убран 24.05 — он искал только по
              // видимым колонкам строки заявки (партномеров там нет), а серверный
              // поиск умеет искать и по партномеру позиции. Иначе вставка
              // партномера давала No Matching Rows даже когда бэк нашёл заявку.
              // getRowId — чтобы при подгрузке следующей пачки AG Grid делал
              // дельта-обновление и НЕ сбрасывал скролл в начало таблицы.
              getRowId={(p) => p.data.id}
              onGridReady={onGridReady}
              // Пересчёт счётчика активных/суммы КП после фильтрации и догрузки.
              onModelUpdated={(e) => recomputeActiveStats(e.api)}
              onCellMouseDown={onCellMouseDown}
              onCellMouseOver={onCellMouseOver}
              onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
              onColumnResized={(e: ColumnResizedEvent) => {
                if (e.finished) saveState(e.api)
              }}
              onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
              onFilterChanged={(e: FilterChangedEvent) => saveState(e.api)}
              rowHeight={40}
              headerHeight={40}
              suppressDragLeaveHidesColumns
              suppressCellFocus
              overlayNoRowsTemplate={
                isProcurement || isSalesView || isRosView
                  ? '<span class="text-sm text-gray-400">Нет активных заявок</span>'
                  : '<span class="text-sm text-gray-400">Нет заявок</span>'
              }
            />
          </div>
          <div ref={sentinelRef} className="h-4" />
          {/* Высота зарезервирована всегда — индикатор не появляется/исчезает,
              поэтому блок «Обработанные» под ним не дёргается при догрузке. */}
          <div className="h-8 leading-8 text-center text-sm text-gray-500">
            {isFetchingNextPage ? 'Загрузка...' : ''}
          </div>
          {(isProcurement || isSalesView || isRosView) && (
            <div ref={processedSectionRef} className="mt-4 scroll-mt-2">
              <button
                onClick={() => setShowProcessed((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                <ProcessedSummaryLabel
                  showProcessed={showProcessed}
                  filtersOn={baseFiltersOn || processedStats.filtered}
                  stats={processedStats}
                  fallbackCount={processedRows.length}
                  summary={summary}
                  isSalesView={isSalesView}
                />
                <span className="text-xs text-gray-500">
                  {isSalesView
                    ? 'Заявки «Счёт», «В отказ» и «В архиве»'
                    : isRosView
                      ? 'Заявки «Счёт» и «В архиве»'
                      : 'Заявки с отправленным КП — в «Выбрать все» не входят'}
                </span>
              </button>
              {showProcessed && (
                <>
                  <div
                    ref={processedGridContainerRef}
                    className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-140px)] mt-2"
                  >
                    <AgGridReact
                      ref={processedGridRef}
                      rowData={processedRows}
                      columnDefs={colDefs}
                      defaultColDef={defaultColDefProcessed}
                      localeText={AG_GRID_LOCALE_RU}
                      getRowId={(p) => p.data.id}
                      onModelUpdated={(e) => recomputeProcessedStats(e.api)}
                      onCellMouseDown={onCellMouseDownProcessed}
                      onCellMouseOver={onCellMouseOverProcessed}
                      rowHeight={40}
                      headerHeight={40}
                      suppressDragLeaveHidesColumns
                      suppressCellFocus
                      overlayNoRowsTemplate='<span class="text-sm text-gray-400">Нет обработанных заявок</span>'
                    />
                  </div>
                  {/* Запас прокрутки: без него блок обработанных в конце
                      страницы не может уехать к верху — его шапка зависает
                      в середине экрана (контента под ним нет). */}
                  <div aria-hidden className="h-[45vh]" />
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
